import React from "react";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Box, Chip } from "@mui/material";

import { style } from "./style";

import GetImages from "@/components/GetImages";
import FormatMentionText from "@/components/Chat/FormatMentionText/FormatMentionText";

import { IRoleType } from "@/models/role";
import { CONVERSATION_TYPE, CHAT_CARD_TYPE } from "@/models/chat";
import { ISessionKeys } from "@/models";

import { storage } from "@/utils/Storage";

type Props = {
  cardType?: CHAT_CARD_TYPE;
  userName: string;
  profilePicture: string;
  lastMessage: string;
  lastMessageTime: string | null;
  isSelected: boolean;
  unreadMessageCount: number;
  mentionCount?: number;
  onSelect: () => void;
  type?: CONVERSATION_TYPE;
  roomName?: string;
  bedNumber?: string;
  isFavorite?: boolean;
  mentions?: any[];
};

const ChatCard = ({
  cardType = CHAT_CARD_TYPE.CHAT,
  userName,
  profilePicture,
  lastMessage,
  lastMessageTime,
  isSelected = false,
  onSelect,
  unreadMessageCount,
  type,
  roomName,
  bedNumber,
  isFavorite,
  mentionCount,
  mentions,
}: Props) => {
  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const isSupportChat = cardType === CHAT_CARD_TYPE.SUPPORT;

  return (
    <Grid
      container
      p={2}
      borderBottom={"0.1rem solid var(--gray-200)"}
      sx={{
        textAlign: "initial",
        color: "var(--black-900)",
        borderRadius: "0",
        cursor: "pointer",
        backgroundColor: {
          md: isSelected ? "var(--warm-neutral-50)" : "transparent",
        },
      }}
      justifyContent={{ xs: "flex-start", md: "center" }}
      alignItems={"center"}
      onClick={onSelect}
    >
      <Grid item xs={2} sm={1} md={2}>
        {isSupportChat ? (
          <Box
            sx={{
              borderRadius: "2.8rem",
              background: "var(--orange-light-hover)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "4rem",
              height: "4rem",
            }}
          >
            <GetImages name="SupportChatIcon" width={"24"} height={"24"} />
          </Box>
        ) : (
          <Avatar
            sx={{ width: 40, height: 40 }}
            alt={userName}
            src={profilePicture}
          />
        )}
      </Grid>
      <Grid item xs={8} sm={8} md={7}>
        <Stack direction={"column"}>
          <Typography variant="subtitle2" display={"flex"} fontWeight={400}>
            {userName}{" "}
            {(roleType === IRoleType.DOCTOR ||
              roleType === IRoleType.JR_DOCTOR) &&
              type === CONVERSATION_TYPE.IN_PATIENT &&
              roomName &&
              bedNumber && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <Chip
                    variant="filled"
                    label={`${roomName} Bed-${bedNumber}`}
                    sx={{
                      bgcolor: "var(--gray-20)",
                      color: "var(--gray-400)",
                      py: 0,
                      mx: "0.6rem",
                      height: "2rem",
                    }}
                  />
                  {isFavorite && (
                    <GetImages
                      name="FavoriteChatListIcon"
                      width={"16"}
                      height={"16"}
                    />
                  )}
                </Stack>
              )}
          </Typography>
          {lastMessage && (
            <Typography
              variant="caption"
              sx={{
                color: lastMessage ? "var(--gray-400)" : "var(--orange-500)",
                fontWeight: lastMessage ? 400 : 500,
              }}
              noWrap
            >
              <FormatMentionText text={lastMessage} mentions={mentions} />
            </Typography>
          )}
        </Stack>
      </Grid>
      <Grid item xs={2} sm={2} md={3}>
        <Stack justifyContent={"center"} alignItems={"center"}>
          {unreadMessageCount > 0 ? (
            <Grid
              item
              display={"flex"}
              flexDirection={"column"}
              alignItems={"flex-end"}
              gap={0.4}
            >
              <Typography component={"p"} sx={style.messageTime}>
                {lastMessageTime}
              </Typography>
              <Grid
                item
                sx={
                  mentionCount && mentionCount > 0
                    ? style.messageStartMentionUnReadCount
                    : style.messageUnReadCount
                }
              >
                {mentionCount > 0 && (
                  <Grid item>
                    <GetImages name="StarMentionIcon" width="11" height="10" />
                  </Grid>
                )}
                <Typography component={"p"} sx={style.messageUnReadCountText}>
                  {mentionCount && mentionCount > 0
                    ? mentionCount
                    : unreadMessageCount}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <>
              {lastMessageTime && (
                <Typography variant="caption" noWrap>
                  {lastMessageTime}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ChatCard;
